/* Copyright (c) 2018-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Injectable } from '@angular/core';
import { Observable, combineLatest, ReplaySubject, of } from 'rxjs';
import { map, take, tap, distinctUntilChanged, catchError } from 'rxjs/operators';
import { isEqual } from 'lodash';
import { Commands } from 'bridge-commands/commands';
import { generateUuid, shallowCompare } from 'utils/lib/common/common-utils';
import { SingleEventStream } from 'utils/lib/event-stream/event-streams';
import { SingleEventStreamFactoryService } from 'utils/lib/event-stream-factory/event-stream-factory.service';
import { SysteminfoTypes } from 'bridge-commands/types/common/systeminfo.types';
import { BridgeService } from '../bridge/bridge.service';
import { Logger, LoggingService } from '../logging/logging.service';
import { DeviceOSType, Undefined } from '../platform-detector/platform-detector';
import { PlatformDetectorService } from '../platform-detector/platform-detector.service';
import { OS, OSTypes } from './models/OS';
import * as SIT from './system-info.models';

// 30 seconds
const SystemInfoCacheLifetime = 30000;
const ClientHeaderInfoCacheLifetime = 30000;

@Injectable({
    providedIn: 'root'
})
export class SystemInfoService {
    static readonly BrowserNameNative = 'Chrome';
    private logger: Logger;
    private clientHeaderInfoStream: SingleEventStream<SIT.SystemInfo>;

    private previousSystemInfo: SIT.SystemInfo = {};
    private systemInfoGuid = generateUuid();
    private systemInfoGuidSubject = new ReplaySubject<string>(1);

    private streams: Map<SIT.SystemInfoKey, SingleEventStream<SIT.SystemInfo>> = new Map<
        SIT.SystemInfoKey,
        SingleEventStream<SIT.SystemInfo>
    >();

    constructor(
        loggingService: LoggingService,
        private bridgeService: BridgeService,
        eventStreamFactory: SingleEventStreamFactoryService
    ) {
        this.logger = loggingService.getLogger('crimson/SystemInfoService');
        this.clientHeaderInfoStream = eventStreamFactory.createStream<SIT.SystemInfo>(
            () => this.fetchClientHeadersInfo(),
            true,
            true,
            true,
            ClientHeaderInfoCacheLifetime
        );

        Object.values(SIT.SystemInfoKey).forEach(key => {
            if (SIT.StaticSystemInfoKeyList.includes(key)) {
                this.streams.set(
                    key,
                    eventStreamFactory.createStream<SIT.SystemInfo>(() => this.fetchSpecificSystemInfo(key))
                );
            } else {
                this.streams.set(
                    key,
                    eventStreamFactory.createStream<SIT.SystemInfo>(
                        () => this.fetchSpecificSystemInfo(key),
                        true,
                        true,
                        true,
                        SystemInfoCacheLifetime
                    )
                );
            }
        });

        // Subscribing staticSystem initially to cache and maintain it.
        this.getSystemInfo(SIT.PrefetchSystemInfoKeyList).subscribe();
    }

    public checkStreamingSupport(): Observable<boolean> {
        return this.bridgeService
            .executeCommand(Commands.Common.Systeminfo.GetStreamingSupportInfo)
            .pipe(
                map(
                    (streamingSupportInfo: SysteminfoTypes.StreamingSupportInfo) =>
                        streamingSupportInfo.IsStreamingSupported
                )
            );
    }

    private fetchDeviceId(): Observable<SIT.SystemInfo> {
        return this.bridgeService.executeCommand(Commands.Common.Systeminfo.GetDeviceId).pipe(
            map((val: SysteminfoTypes.DeviceIDInfo) => {
                const deviceIdInfo: SIT.SystemInfo = {
                    DeviceId: val.DeviceID
                };
                return deviceIdInfo;
            })
        );
    }

    public getSystemInfoGuid(): Observable<string> {
        return this.systemInfoGuidSubject.asObservable();
    }

    private updateSystemInfoGuid(systemInfo: SIT.SystemInfo): void {
        if (!isEqual(systemInfo, this.previousSystemInfo)) {
            this.systemInfoGuid = generateUuid();
            this.previousSystemInfo = systemInfo;
        }
        this.systemInfoGuidSubject.next(this.systemInfoGuid);
    }

    private fetchClientHeadersInfo(): Observable<SIT.SystemInfo> {
        /**
         * To Do: Android can also have a new bridge command just for client headers
         * That would eliminate 'extractDeviceTypeForBrowserClient' which is now being used
         * by Native Android Client
         */
        return PlatformDetectorService.isAndroidPlatform()
            ? this.bridgeService.executeCommand(Commands.Common.Systeminfo.GetSystemInfo)
            : this.bridgeService.executeCommand(Commands.Common.Systeminfo.GetClientHeadersInfo);
    }

    private fetchSystemInfo(keys: SIT.SystemInfoKey[]): Observable<SIT.SystemInfo> {
        const systemInfoKey = new Set(keys);
        const fetchSystemInfo$ = [];

        // Currently deviceId is not part of system info
        if (systemInfoKey.delete(SIT.SystemInfoKey.DeviceId)) {
            const fetchDeviceId$ = this.fetchDeviceId();
            fetchSystemInfo$.push(fetchDeviceId$);
        }

        /**
         * BrowserName and BrowserVersion on cef are is not
         * a part of system info
         */
        if (
            /* Platform-check-disable-next-line */
            PlatformDetectorService.isCefPlatform() &&
            (systemInfoKey.delete(SIT.SystemInfoKey.BrowserName) ||
                systemInfoKey.delete(SIT.SystemInfoKey.BrowserVersion))
        ) {
            const fetchBrowserInfo$ = this.fetchBrowserInfo();
            fetchSystemInfo$.push(fetchBrowserInfo$);
        }

        if (systemInfoKey.size) {
            const fetchSystemInfoForKeys$: Observable<SIT.SystemInfo> = this.bridgeService
                .executeCommand(Commands.Common.Systeminfo.GetSystemInfo, {
                    keys: [...systemInfoKey]
                })
                .pipe(
                    map(info => {
                        delete info['_return_code'];
                        delete info['_return_status'];
                        delete info['_return_internal'];
                        return info;
                    })
                );

            fetchSystemInfo$.push(fetchSystemInfoForKeys$);
        }

        return combineLatest([...fetchSystemInfo$]).pipe(
            map((values: SIT.SystemInfo[]) => {
                let systemInfo: SIT.SystemInfo = {};
                values.forEach(value => {
                    systemInfo = {
                        ...systemInfo,
                        ...value
                    };
                });
                return systemInfo;
            }),
            tap((systemInfo: SIT.SystemInfo) => {
                const currentSystemInfo = { ...this.previousSystemInfo, ...systemInfo };
                this.updateSystemInfoGuid(currentSystemInfo);
            })
        );
    }

    // For cef native only
    private fetchBrowserInfo(): Observable<SIT.SystemInfo> {
        return this.bridgeService.executeCommand(Commands.Common.Systeminfo.GetBrowserInfo).pipe(
            map(value => {
                const browserInfo: SIT.SystemInfo = {
                    BrowserName: value?.BrowserNameNative || Undefined,
                    BrowserVersion: value?.BrowserVersionNative || Undefined
                };

                return browserInfo;
            })
        );
    }

    private fetchSpecificSystemInfo(key: SIT.SystemInfoKey): Observable<SIT.SystemInfo> {
        return this.fetchSystemInfo([key]);
    }

    /**
     * @param keys takes all the keys for which values needs to be requested
     * via systemInfo call
     **/
    public getSystemInfo(keys: SIT.SystemInfoKey[], refresh = false): Observable<SIT.SystemInfo> {
        if (refresh) {
            return this.fetchSystemInfo(keys);
        }

        const keysToRequest = [];
        const cachedSystemInfo = {};
        keys.forEach((key: SIT.SystemInfoKey) => {
            const systemInfoData = this.streams.get(key)?.getData();
            if (systemInfoData && systemInfoData[key]) {
                cachedSystemInfo[key] = systemInfoData[key];
            } else {
                keysToRequest.push(key);
            }
        });

        if (keysToRequest.length === 0) {
            return of(cachedSystemInfo);
        }

        return this.fetchSystemInfo(keysToRequest).pipe(
            map(systemInfo => ({ ...cachedSystemInfo, ...systemInfo })),
            tap((systemInfo: SIT.SystemInfo) => {
                Object.keys(systemInfo).forEach((key: SIT.SystemInfoKey) => {
                    if (this.streams.has(key)) {
                        this.streams.get(key).setData(systemInfo, true);
                    } else {
                        this.logger.error('No systemInfo stream for key ', key);
                    }
                });
            })
        );
    }

    public fetchSystemArchitecture(): Observable<string> {
        return this.getSystemInfo([SIT.SystemInfoKey.ProcessorArchitecture]).pipe(
            take(1),
            map(systemInfo => systemInfo.ProcessorArchitecture)
        );
    }

    public isArch64ProcessorArchitecture(): Observable<boolean> {
        return this.fetchSystemArchitecture().pipe(
            map(architecture => architecture === SIT.Arch64),
            catchError(error => {
                this.logger.error('Failed to get ProcessorArchitecture', error);
                return of(false);
            })
        );
    }

    public getClientHeadersInfo(refresh = false): Observable<SIT.SystemInfo> {
        if (refresh) {
            return this.clientHeaderInfoStream.refresh().pipe(take(1));
        }
        return this.clientHeaderInfoStream.get$().pipe(take(1));
    }

    // Function 'getDeviceOSType' is added to return DeviceOSType for both Native and browser client.
    // Existing 'getOsInformation' can not be used since string returned by it is not consistent for
    // Windows and Mac platforms on both clients.
    public getDeviceOSType(): Observable<DeviceOSType> {
        return this.getSystemInfo([SIT.SystemInfoKey.OSName, SIT.SystemInfoKey.Platform]).pipe(
            map(systemInfo => {
                if (PlatformDetectorService.isBrowserLikePlatform()) {
                    return systemInfo.OSName as DeviceOSType;
                } else {
                    if (systemInfo.Platform === OSTypes.Mac) {
                        return DeviceOSType.MacOS;
                    } else if (systemInfo.Platform === OSTypes.Windows) {
                        return DeviceOSType.Windows;
                    } else if (systemInfo.Platform === OSTypes.Linux) {
                        /* Platform-check-disable-next-line */
                        // While SteamOS is a linux distribution, downstream flows have different
                        // handling for SteamOS vs Linux. So we return a different OSType
                        return systemInfo.OSName === DeviceOSType.SteamOS ? DeviceOSType.SteamOS : DeviceOSType.Linux;
                    } else {
                        return DeviceOSType.Undefined;
                    }
                }
            })
        );
    }

    /**
     * Emits the current network or whenever network is changed if supported.
     * - Combine the current `Network` object with the updated network type
     *
     * Note: `GetNetworkTypeChangedInfo` command is only available in browser clients.
     *
     * @returns Network object in SystemInfo
     */
    public onNetworkChanged(): Observable<SIT.Network> {
        const currentNetwork$ = this.getSystemInfo([SIT.SystemInfoKey.Network]);
        /* Platform-check-disable-next-line */
        if (PlatformDetectorService.isBrowserPlatform()) {
            const network$ = combineLatest([
                currentNetwork$,
                this.bridgeService.executeCommand(Commands.Common.Systeminfo.GetNetworkTypeChangedInfo)
            ]).pipe(
                map(([systemInfo, updatedNetworkInfo]) => {
                    const currentNetwork = systemInfo.Network;
                    return {
                        ...currentNetwork,
                        NetworkType: updatedNetworkInfo.networkType
                    };
                })
            );
            return network$.pipe(distinctUntilChanged(shallowCompare));
        }
        return currentNetwork$.pipe(map(systemInfo => systemInfo.Network));
    }
}
